import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, MapPin, Sparkles, Waves, Users, Home, Wine, Heart } from "lucide-react";

const highlights = [
  { icon: Home, text: "Private luxury villa" },
  { icon: Waves, text: "Pool & leisure spaces" },
  { icon: Users, text: "Ideal for groups & families" },
  { icon: Sparkles, text: "Premium interiors" },
  { icon: Wine, text: "Weekend getaway destination" },
  { icon: Heart, text: "Curated hospitality experience" },
];

const WA = "https://wa.me/919091100200";

export default function Villas() {
  return (
    <section id="villas" data-testid="villas-section" className="relative py-24 md:py-32 overflow-hidden bg-offwhite">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 right-0 w-[600px] h-[600px] rounded-full opacity-20" style={{ background: "radial-gradient(circle, #D4AF37, transparent 70%)", filter: "blur(80px)" }} />
        <div className="absolute bottom-1/4 left-0 w-[600px] h-[600px] rounded-full opacity-15" style={{ background: "radial-gradient(circle, #0099CC, transparent 70%)", filter: "blur(80px)" }} />
      </div>

      <div className="relative max-w-7xl mx-auto px-5 md:px-8">
        <div className="max-w-3xl mb-14">
          <div className="text-xs uppercase tracking-[0.3em] text-gold-dark font-semibold mb-4">— Private Stays</div>
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tighter leading-[1.05] text-navy-deep">
            Luxury Villas &{" "}
            <span className="text-gradient-navy">Private Stays</span>.
          </h2>
          <p className="mt-6 text-base md:text-lg text-slate-600 leading-relaxed">
            Exclusive private villas designed for comfort, luxury, celebrations, and memorable getaways.
          </p>
        </div>

        {/* Featured villa card — full-width cinematic */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          data-testid="villa-ivy-house"
          className="relative rounded-[2rem] overflow-hidden bg-white border border-navy-deep/8 premium-shadow"
        >
          <div className="grid lg:grid-cols-12 gap-0">
            {/* Image side */}
            <div className="relative lg:col-span-7 aspect-[16/11] lg:aspect-auto overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=1600&q=85&auto=format"
                alt="Ivy House Villa Virar"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1600ms] hover:scale-[1.04]"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-navy-dark/55 via-navy-dark/15 to-transparent" />

              {/* Featured pill */}
              <div className="absolute top-5 left-5 flex items-center gap-2 px-4 py-2 rounded-full bg-white/95 backdrop-blur shadow-lg">
                <Sparkles className="w-3.5 h-3.5 text-gold-dark" strokeWidth={2.4} />
                <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-navy-deep">Featured Villa</span>
              </div>

              {/* Floating location chip */}
              <div className="absolute bottom-5 left-5 flex items-center gap-2 px-4 py-2.5 rounded-full bg-navy-deep/85 backdrop-blur text-white">
                <MapPin className="w-3.5 h-3.5 text-gold" strokeWidth={2.4} />
                <span className="text-xs font-semibold">Virar · Mumbai</span>
              </div>
            </div>

            {/* Content side */}
            <div className="lg:col-span-5 p-8 md:p-10 lg:p-12 flex flex-col">
              <div className="text-[11px] uppercase tracking-[0.28em] text-sky-brand font-bold mb-3">Private Luxury Villa</div>
              <h3 className="font-heading text-3xl md:text-4xl lg:text-[2.6rem] font-bold tracking-tight leading-[1.05] text-navy-deep mb-5">
                Ivy House <span className="text-gold-dark">—</span> Virar
              </h3>
              <p className="text-slate-600 leading-relaxed text-[15px] mb-7">
                A luxurious private villa experience perfect for staycations, celebrations, and peaceful escapes near Mumbai.
              </p>

              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3 mb-8">
                {highlights.map((h) => {
                  const Icon = h.icon;
                  return (
                    <li key={h.text} className="flex items-center gap-2.5 text-[13.5px] text-navy-deep/85 font-medium">
                      <span className="w-7 h-7 rounded-lg bg-sky-soft flex items-center justify-center shrink-0">
                        <Icon className="w-3.5 h-3.5 text-sky-brand" strokeWidth={2.2} />
                      </span>
                      {h.text}
                    </li>
                  );
                })}
              </ul>

              <div className="flex flex-col sm:flex-row gap-3 mt-auto">
                <a
                  href={WA}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="villa-cta-view"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-white border-2 border-navy-deep/15 text-navy-deep font-semibold text-sm hover:border-sky-brand/60 hover:bg-sky-soft/40 transition-all soft-shadow"
                >
                  View Villa
                </a>
                <a
                  href={WA}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="villa-cta-book"
                  className="group inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full gradient-navy text-white font-semibold text-sm shadow-[0_12px_28px_-8px_rgba(0,51,102,0.45)] hover:shadow-[0_18px_36px_-8px_rgba(0,51,102,0.6)] hover:scale-[1.02] transition-all"
                >
                  Book Your Stay
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </a>
              </div>
            </div>
          </div>
        </motion.div>

        {/* "More coming soon" hint */}
        <div className="mt-8 text-center">
          <p className="text-xs uppercase tracking-[0.28em] text-slate-400 font-medium">More curated villas launching soon</p>
        </div>
      </div>
    </section>
  );
}
